import React, { useState, useEffect } from 'react';
import { CollectionModel } from '../types/models';

export type UploadMode = 'auto' | 'new' | 'existing';

export interface DragAndDropResult {
    files: File[];
    mode: UploadMode;
    targetCollectionId?: string;
    newCollectionName?: string;
    isMagazineImport?: boolean;
}

interface DragAndDropModalProps {
    files: File[];
    folders?: string[];
    collections?: CollectionModel[];
    isVLMSupported?: boolean;
    onConfirm: (result: DragAndDropResult) => void;
    onCancel: () => void;
}

const SUPPORTED_EXTENSIONS = [
    '.docx', '.doc', '.txt', '.log', '.ini', '.rtf', '.odt',
    '.xls', '.xlsx', '.csv', '.pptx', '.pdf', '.epub',
    '.htm', '.html', '.md', '.json', '.xml'
];
const SUPPORTED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.bmp'];
const SUPPORTED_ARCHIVE_EXTENSIONS = ['.mag'];

interface FileTreeNode {
    name: string;
    path: string;
    isFile: boolean;
    file?: File;
    children: FileTreeNode[];
    size: number;
}

const getAllFiles = (node: FileTreeNode): File[] => {
    if (node.isFile && node.file) return [node.file];
    return node.children.flatMap(getAllFiles);
};

const buildFileTree = (files: File[]): FileTreeNode[] => {
    const root = new Map<string, any>();

    files.forEach(file => {
        const path = (file as any).webkitRelativePath || (file as any).itemPath || file.name;
        const parts = path.split('/');
        let currentLevel = root;

        parts.forEach((part: string, index: number) => {
            const isFile = index === parts.length - 1;
            
            if (!currentLevel.has(part)) {
                currentLevel.set(part, {
                    name: part,
                    path: parts.slice(0, index + 1).join('/'),
                    isFile,
                    file: isFile ? file : undefined,
                    children: new Map(),
                    size: 0
                });
            }
            
            const node = currentLevel.get(part);
            if (!isFile) {
                currentLevel = node.children;
            }
        });
    });

    const processNode = (node: any): FileTreeNode => {
        const children = Array.from(node.children.values()).map(processNode);
        
        children.sort((a: any, b: any) => {
            if (a.isFile === b.isFile) return a.name.localeCompare(b.name);
            return a.isFile ? 1 : -1;
        });

        const size = node.isFile ? node.file.size : children.reduce((acc: number, child: any) => acc + child.size, 0);

        return {
            name: node.name,
            path: node.path,
            isFile: node.isFile,
            file: node.file,
            children,
            size
        };
    };

    return Array.from(root.values()).map(processNode).sort((a, b) => {
         if (a.isFile === b.isFile) return a.name.localeCompare(b.name);
         return a.isFile ? 1 : -1;
    });
};

function FileTreeItem({
    node,
    level,
    selectedFiles,
    onToggle,
    formatSize,
}: {
    node: FileTreeNode;
    level: number;
    selectedFiles: Set<File>;
    onToggle: (node: FileTreeNode) => void;
    formatSize: (bytes: number) => string;
}) {
    const [isOpen, setIsOpen] = useState(true);

    const getFileType = (fileName: string): 'document' | 'image' | 'archive' | 'unknown' => {
        const ext = `.${fileName.split('.').pop()?.toLowerCase() || ''}`;
        if (SUPPORTED_ARCHIVE_EXTENSIONS.includes(ext)) return 'archive';
        if (SUPPORTED_EXTENSIONS.includes(ext)) return 'document';
        if (SUPPORTED_IMAGE_EXTENSIONS.includes(ext)) return 'image';
        return 'unknown';
    };

    const allNodeFiles = React.useMemo(() => getAllFiles(node), [node]);
    
    const selectionState = React.useMemo(() => {
        if (allNodeFiles.length === 0) return 'unchecked';
        const selectedCount = allNodeFiles.filter(f => selectedFiles.has(f)).length;
        if (selectedCount === allNodeFiles.length) return 'checked';
        if (selectedCount === 0) return 'unchecked';
        return 'indeterminate';
    }, [allNodeFiles, selectedFiles]);

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggle(node);
    };

    return (
        
        <div>
            {/* Dragged Folder(s) architecture node renderer */}
            <div 
                className="flex items-center px-2 py-1 hover:bg-gray-800/50 transition-colors cursor-pointer select-none border-b border-gray-800/50"
                style={{ paddingLeft: `${level * 1.5 + 0.5}rem` }}
                onClick={() => !node.isFile && setIsOpen(!isOpen)}
            >
                <div className="w-4 h-4 mr-1 flex items-center justify-center text-gray-500">
                    {!node.isFile && (
                        <i className={`fas fa-chevron-right text-xs transition-transform duration-200 ${isOpen ? 'transform rotate-90' : ''}`} />
                    )}
                </div>

                <div 
                    className={`w-4 h-4 mr-2 rounded border flex items-center justify-center transition-colors flex-shrink-0
                        ${selectionState === 'checked' ? 'bg-red-600 border-red-600' : 
                          selectionState === 'indeterminate' ? 'bg-red-600 border-red-600' : 
                          'border-gray-600 bg-transparent hover:border-gray-500'}`}
                    onClick={handleToggle}
                >
                    {selectionState === 'checked' && (
                        <i className="fas fa-check text-white text-xs" />
                    )}
                    {selectionState === 'indeterminate' && (
                        <div className="w-2 h-0.5 bg-white rounded-full" />
                    )}
                </div>

                <div className="mr-2 text-gray-400">
                    {node.isFile ? (
                        getFileType(node.name) === 'archive' ? (
                            <i className="fas fa-file-archive text-purple-400 text-sm" />
                        ) : getFileType(node.name) === 'image' ? (
                            <i className="fas fa-regular fa-image text-blue-500 text-sm" />
                        ) : (
                            <i className="fas fa-file-alt text-white text-sm" />
                        )
                    ) : (
                        <i className="fas fa-folder text-yellow-500 text-sm" />
                    )}
                </div>

                <span className="text-gray-200 text-sm truncate flex-1">
                    {node.name}
                </span>

                <span className="text-xs text-gray-500 font-mono ml-2">
                    {formatSize(node.size)}
                </span>
            </div>

            {!node.isFile && isOpen && (
                <div>
                    {node.children.map(child => (
                        <FileTreeItem 
                            key={child.path} 
                            node={child} 
                            level={level + 1} 
                            selectedFiles={selectedFiles}
                            onToggle={onToggle}
                            formatSize={formatSize}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const DragAndDropModal = function DragAndDropModal({ files, folders = [], collections, isVLMSupported = false, onConfirm, onCancel }: DragAndDropModalProps) {
    const [selectedFiles, setSelectedFiles] = useState<Set<File>>(new Set());
    const [filter, setFilter] = useState<'all' | 'documents' | 'images'>('all');
    
    // Upload configuration state
    const [uploadMode, setUploadMode] = useState<UploadMode>(folders.length > 0 ? 'auto' : 'new');
    const [targetCollectionId, setTargetCollectionId] = useState<string>('');
    const [newCollectionName, setNewCollectionName] = useState<string>('');

    useEffect(() => {
        if (!collections) return;
        const userCollections = collections.filter(c => c.id !== '-1');
        if (userCollections.length > 0) {
            setTargetCollectionId(userCollections[0].id);
        }
    }, [collections]);

    useEffect(() => {
        // Filter files based on support
        const validFiles = files.filter(file => {
            const ext = `.${  file.name.split('.').pop()?.toLowerCase() || ''}`;
            if (SUPPORTED_ARCHIVE_EXTENSIONS.includes(ext)) return true;
            if (SUPPORTED_EXTENSIONS.includes(ext)) return true;
            if (isVLMSupported && SUPPORTED_IMAGE_EXTENSIONS.includes(ext)) return true;
            return false;
        });
        setSelectedFiles(new Set(validFiles));
    }, [files, isVLMSupported]);

    const getFileCategory = (file: File): 'document' | 'image' | 'archive' | 'unknown' => {
        const ext = `.${  file.name.split('.').pop()?.toLowerCase() || ''}`;
        if (SUPPORTED_ARCHIVE_EXTENSIONS.includes(ext)) return 'archive';
        if (SUPPORTED_EXTENSIONS.includes(ext)) return 'document';
        if (SUPPORTED_IMAGE_EXTENSIONS.includes(ext)) return 'image';
        return 'unknown';
    };

    const filteredFiles = React.useMemo(() => files.filter(file => {
        const category = getFileCategory(file);
        if (filter === 'all') return (category !== 'unknown') && (isVLMSupported || category !== 'image');
        if (filter === 'documents') return category === 'document' || category === 'archive';
        if (filter === 'images') return category === 'image' && isVLMSupported;
        return false;
    }), [files, filter, isVLMSupported]);

    const fileTree = React.useMemo(() => buildFileTree(filteredFiles), [filteredFiles]);

    const handleNodeToggle = (node: FileTreeNode) => {
        const nodeFiles = getAllFiles(node);
        const allSelected = nodeFiles.every(f => selectedFiles.has(f));
        
        const newSelection = new Set(selectedFiles);
        if (allSelected) {
            nodeFiles.forEach(f => newSelection.delete(f));
        } else {
            nodeFiles.forEach(f => newSelection.add(f));
        }
        setSelectedFiles(newSelection);
    };

    const toggleAll = () => {
        const allFilteredSelected = filteredFiles.every(f => selectedFiles.has(f));
        const newSelection = new Set(selectedFiles);
        
        if (allFilteredSelected) {
            filteredFiles.forEach(f => newSelection.delete(f));
        } else {
            filteredFiles.forEach(f => newSelection.add(f));
        }
        setSelectedFiles(newSelection);
    };

    const syncSelection = React.useMemo(() => {
      // Ensure selectedFiles only contains files in filteredFiles
        return filteredFiles.filter(f => selectedFiles.has(f));
    }, [filteredFiles, selectedFiles]);

    const hasMagazineFiles = React.useMemo(() => {
        return syncSelection.some(f => {
            const ext = `.${  f.name.split('.').pop()?.toLowerCase() || ''}`;
            return SUPPORTED_ARCHIVE_EXTENSIONS.includes(ext);
        });
    }, [syncSelection]);

    const totalSize = syncSelection.reduce((acc, file) => acc + file.size, 0);
    
    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / k**i).toFixed(2))} ${sizes[i]}`;
    };

    const handleConfirm = () => {
        onConfirm({
            files: syncSelection,
            mode: uploadMode,
            targetCollectionId: uploadMode === 'existing' ? targetCollectionId : undefined,
            newCollectionName: uploadMode === 'new' ? newCollectionName : undefined,
            isMagazineImport: hasMagazineFiles,
        });
    };

    const isConfirmDisabled = () => {
        if (syncSelection.length === 0) return true;

        // Magazine imports skip collection destination validation
        if (hasMagazineFiles) return false;

        // Only validate collection settings if collections are provided
        if (collections) {
            if (uploadMode === 'new' && !newCollectionName.trim()) return true;
            if (uploadMode === 'existing' && !targetCollectionId) return true;
        }

        return false;
    };

    return (
    <div className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 pt-12">
      <div className="rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden border border-gray-800">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-5 py-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="rounded-lg backdrop-blur-sm">
              <i className="fas fa-cloud-upload-alt text-white text-lg" />
            </div>
            <h2 className="text-xl font-semibold text-white">
              Upload
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5 flex-1 flex flex-col min-h-0 overflow-y-auto">
          {/* Magazine Import Banner */}
          {hasMagazineFiles && (
          <div className="bg-purple-900/30 p-4 rounded-xl border border-purple-700/50 flex-shrink-0">
              <div className="flex items-center gap-2 mb-1">
                  <i className="fas fa-file-archive text-purple-400" />
                  <span className="text-sm font-semibold text-purple-200">Magazine Import</span>
              </div>
              <p className="text-xs text-purple-300/80">
                  Magazine files contain predefined collections and chat histories. They will be automatically restored to your workspace.
              </p>
          </div>
          )}

          <div role="note" className="bg-neutral-600/50 p-4 rounded-large border border-neutral-400 space-y-2">
            <p className="font-semibold">Sensitive data protection</p>
            <p className="text-sm">New documents are scanned locally. Detected PII/PHI is replaced before indexing.
              Only masked text is saved; original files and their formatting cannot be recovered here.</p>
            <p className="text-sm">Detection can miss information. Review masked content before sharing.
              Magazine imports are unavailable while this protection is enabled.</p>
          </div>
          {/* Upload Settings */}
          {collections && !hasMagazineFiles && (
          <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700 space-y-3 flex-shrink-0">
              <h3 className="text-sm font-semibold text-gray-200">Upload Destination</h3>
              
              <div className="flex flex-col gap-3">
                  <select
                      value={uploadMode}
                      onChange={(e) => setUploadMode(e.target.value as UploadMode)}
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-red-500 transition-colors"
                  >
                      {folders.length > 0 && <option value="auto">Auto Create Collection</option>}
                      <option value="new">Create New Collection</option>
                      <option value="existing">Add to Existing Collection</option>
                  </select>

                  {/* Mode-specific additional inputs */}
                  {uploadMode === 'auto' && (
                      <div className="text-xs text-gray-400 px-1">
                          Creates collections based on folder names / &quot;Dropped Files&quot; for multiple files.
                      </div>
                  )}

                  {uploadMode === 'new' && (
                      <input
                          type="text"
                          value={newCollectionName}
                          onChange={(e) => setNewCollectionName(e.target.value)}
                          placeholder="Enter collection name..."
                          className="w-full bg-black/40 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
                      />
                  )}

                  {uploadMode === 'existing' && (
                      <select
                          value={targetCollectionId}
                          onChange={(e) => setTargetCollectionId(e.target.value)}
                          className="w-full bg-black/40 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-red-500 transition-colors"
                      >
                          {collections
                              .filter(c => c.id !== '-1')
                              .map(c => (
                                  <option key={c.id} value={c.id} className="bg-gray-800">
                                      {c.name}
                                  </option>
                              ))
                          }
                      </select>
                  )}
              </div>
          </div>
          )}

          {/* Files List */}
          <div className="space-y-2 flex-1 flex flex-col min-h-[10rem]">
            <div className="flex justify-between items-center flex-shrink-0">
                <div className="flex gap-2">
                    <button
                        onClick={() => setFilter('all')}
                        className={`text-xs font-medium px-2 py-1 rounded transition-colors ${filter === 'all' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'}`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setFilter('documents')}
                        className={`text-xs font-medium px-2 py-1 rounded transition-colors ${filter === 'documents' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'}`}
                    >
                        Documents
                    </button>
                    {isVLMSupported && (
                        <button
                            onClick={() => setFilter('images')}
                            className={`text-xs font-medium px-2 py-1 rounded transition-colors ${filter === 'images' ? 'bg-red-600 text-white' : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'}`}
                        >
                            Images
                        </button>
                    )}
                </div>
                <button 
                    onClick={toggleAll}
                    className="text-xs text-red-400 hover:text-red-300 font-medium transition-colors"
                >
                    {filteredFiles.length > 0 && filteredFiles.every(f => selectedFiles.has(f)) ? 'Deselect All' : 'Select All'}
                </button>
            </div>
            {folders.length > 0 && (
                <div className="text-xs text-gray-400 px-1 flex-shrink-0">
                  <span className="font-semibold text-gray-500">Source Folders:</span> {folders.join(', ')}
                </div>
            )}
            
            <div className="border border-gray-800 rounded-xl overflow-hidden bg-black/40 flex-1 flex flex-col min-h-0">
              <div className="overflow-y-auto flex-1">
                {fileTree.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                        No {filter === 'all' ? 'supported' : filter} files found.
                    </div>
                ) : (
                    fileTree.map(node => (
                        <FileTreeItem 
                            key={node.path}
                            node={node}
                            level={0}
                            selectedFiles={selectedFiles}
                            onToggle={handleNodeToggle}
                            formatSize={formatSize}
                        />
                    ))
                )}
              </div>
            </div>
          </div>

          {/* Total Size */}
          <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-xl border border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-gray-300 text-sm font-semibold">Total File(s):</span>
              <span className="text-base font-bold text-white font-mono">
                {syncSelection.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-300 text-sm font-semibold">Total Size:</span>
              <span className="text-base font-bold text-white font-mono">
                {formatSize(totalSize)}
              </span>
            </div>
          </div>
        </div>


        {/* Actions */}
        <div className="px-5 py-3 bg-black/40 border-t border-gray-800 flex gap-3 justify-end flex-shrink-0">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 hover:bg-gray-700 hover:border-gray-600 transition-all duration-200 flex items-center gap-2"
          >
            <i className="fas fa-times text-sm" />
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isConfirmDisabled()}
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-all duration-200 flex items-center gap-2 ${isConfirmDisabled() ? 'bg-gray-700 cursor-not-allowed opacity-50' : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg hover:shadow-red-900/50'}`}
          >
            <i className="fas fa-cloud-upload-alt text-sm" />
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default DragAndDropModal;
